import { EcoCard, EcoSkeleton, EcoText } from '@components/shared';
import { MerchantAdmin } from '@ecocart/entities';
import { Gap } from '@utils/layout';
import { colors, spacing } from '@utils/tailwind';
import { format } from 'date-fns';
import React from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { twMerge } from 'tailwind-merge';

interface Props {
  merchantAdmin: MerchantAdmin;
}

interface CardProps {
  label: string;
  value: string;
}

const getClass = (value: string): { container: string } => {
  const lcValue = value.toLowerCase();
  if (['invalid', 'inactive', 'hidden'].some((neg) => lcValue.includes(neg))) return { container: 'border border-danger-700 bg-danger-50' };
  if (['valid', 'active', 'visible'].some((pos) => lcValue.includes(pos))) return { container: 'border border-success bg-success-50' };
  return { container: '' };
};

function Card({ label, value }: CardProps): JSX.Element {
  return (
    <View className={twMerge('rounded-sm flex-1 p-4 h-[100px] flex-col justify-center items-center bg-white', getClass(value).container)}>
      <EcoText fallback={<EcoSkeleton mode="dark" height="xl" />} fontSize="xl" fontWeight="semibold">
        {value}
      </EcoText>
      <EcoText fontSize="xs" fallback={<EcoSkeleton mode="dark" height="sm" />} className="capitalize">
        {label}
      </EcoText>
    </View>
  );
}

export default function MerchantActivityCard({ merchantAdmin }: Props): JSX.Element {
  const { shopName, active, createdAt, updatedAt, variantIds, ecocartEnabled, ecocartActive } = merchantAdmin || {};
  const config: CardProps[] = [
    { label: 'Last Updated', value: updatedAt ? format(new Date(updatedAt), 'MMM d, yyyy') : '' },
    { label: 'Initial Install', value: createdAt ? format(new Date(createdAt), 'MMM d, yyyy') : '' },
    { label: 'Store Variants', value: variantIds?.[0] ? `Valid (${variantIds.length})` : 'Invalid' },
    { label: 'EcoCart', value: ecocartActive ? 'Active' : 'Inactive' },
    { label: 'Widgets', value: ecocartEnabled ? 'Visible' : 'Hidden' }
  ];

  return merchantAdmin ? (
    <EcoCard
      title={shopName}
      topRightComponent={
        active ? (
          <EcoText color="success" fontWeight="semibold">
            Installed
          </EcoText>
        ) : (
          <EcoText color="danger" fontWeight="semibold">
            Uninstalled
          </EcoText>
        )
      }
      className={twMerge('border', active ? 'border-success' : 'border-danger-700')}
      style={{ padding: Number(spacing['4'].replace('px', '')), backgroundColor: active ? colors.success[50] : colors.danger[50] }}
    >
      <ScrollView horizontal className="mt-4" contentContainerStyle={{ width: '100%' }}>
        <View className="flex-col lg:flex-row space-evenly w-full" style={Gap()}>
          {config.map(({ label, value }) => (
            <Card key={label} label={label} value={value} />
          ))}
        </View>
      </ScrollView>
    </EcoCard>
  ) : (
    <></>
  );
}
